import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Stack,
  Skeleton,
} from '@mui/material'
import { CheckCircle as ApproveIcon, Download as DownloadIcon } from '@mui/icons-material'
import AttachmentsTab from '../../components/client-detail/AttachmentsTab'
import ResumeFilePreview from '../../components/client-detail/ResumeFilePreview'
import type { PortalSectionProps } from '../../components/portal/portalSection'
import PageHelpButton from '../../components/PageHelpButton'
import { usePortalData } from '../../components/portal/PortalDataProvider'
import { attachmentsApi, clientsApi, publishApi, Attachment, Client } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ResumeData = string | Record<string, any>

// Convert resume content to HTML for display
const resumeToHtml = (content: ResumeData): string => {
  if (!content) return ''

  if (typeof content === 'string') {
    if (content.startsWith('<')) return content
    try {
      const parsed = JSON.parse(content)
      return resumeToHtml(parsed)
    } catch {
      return content
        .split('\n\n')
        .map((paragraph) => {
          const lines = paragraph.split('\n').join('<br/>')
          return `<p>${lines}</p>`
        })
        .join('')
    }
  }

  const sections: string[] = []

  if (content.professionalSummary) {
    sections.push('<h2>Professional Summary</h2>')
    sections.push(`<p>${content.professionalSummary}</p>`)
  }

  if (content.skills?.length) {
    sections.push('<h2>Key Skills</h2>')
    sections.push('<ul>')
    content.skills.forEach((skill: string) => {
      sections.push(`<li>${skill}</li>`)
    })
    sections.push('</ul>')
  }

  if (content.experience?.length) {
    sections.push('<h2>Experience</h2>')
    content.experience.forEach(
      (exp: { title: string; company: string; duration?: string; bullets?: string[] }) => {
        sections.push(`<h3>${exp.title} - ${exp.company}</h3>`)
        if (exp.duration) {
          sections.push(`<p><em>${exp.duration}</em></p>`)
        }
        if (exp.bullets?.length) {
          sections.push('<ul>')
          exp.bullets.forEach((bullet: string) => {
            sections.push(`<li>${bullet}</li>`)
          })
          sections.push('</ul>')
        }
      }
    )
  }

  if (content.additionalSections) {
    Object.entries(content.additionalSections).forEach(([sectionName, sectionContent]) => {
      sections.push(`<h2>${sectionName}</h2>`)
      if (typeof sectionContent === 'string') {
        sections.push(`<p>${sectionContent}</p>`)
      } else if (Array.isArray(sectionContent)) {
        sections.push('<ul>')
        sectionContent.forEach((item: string) => {
          sections.push(`<li>${item}</li>`)
        })
        sections.push('</ul>')
      }
    })
  }

  return sections.join('')
}

export default function PortalResume({ embedded }: PortalSectionProps = {}) {
  const { clientId } = usePortalData()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  // The file of an uploaded resume, once the client record has pointed at it.
  const [resumeFile, setResumeFile] = useState<Attachment | null>(null)
  const [fileError, setFileError] = useState(false)
  const [approving, setApproving] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const resumeRef = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'))
  const { showSuccess, showError } = useNotification()

  const handleApprove = async () => {
    if (!client?.id) return
    setApproving(true)
    try {
      await publishApi.approveResume(client.id)
      setClient({ ...client, resumeApproved: true, resumeApprovedAt: new Date().toISOString() })
      showSuccess('Resume approved successfully!')
    } catch (err) {
      console.error('Failed to approve resume:', err)
      showError('Failed to approve resume')
    } finally {
      setApproving(false)
    }
  }

  const handleDownloadPdf = async () => {
    if (!resumeRef.current || !client) return
    setDownloadingPdf(true)
    try {
      const html2pdf = (await import('html2pdf.js')).default
      const sanitizedName = (client.name || 'Resume')
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(/\s+/g, '_')
      const fileName = `${sanitizedName}_Resume.pdf`

      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: fileName,
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(resumeRef.current)
        .save()

      showSuccess('Resume PDF downloaded')
    } catch (err) {
      console.error('Error downloading resume PDF:', err)
      showError('Failed to download resume as PDF')
    } finally {
      setDownloadingPdf(false)
    }
  }

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const clientData = await clientsApi.get(clientId)
        // An uploaded resume is the file the client record points at. The id is
        // withheld while the resume is a draft, and drafts are not listed.
        if (clientData.resumeSource === 'uploaded' && clientData.resumeAttachmentId) {
          try {
            const files = await attachmentsApi.list(clientId, 'resume_document')
            setResumeFile(
              files.find((a) => a.id === clientData.resumeAttachmentId && a.isActive !== false) ?? null
            )
          } catch (fileErr) {
            console.error('Failed to load the uploaded resume:', fileErr)
            setFileError(true)
          }
        }
        setClient(clientData)
      } catch (err) {
        console.error('Failed to load client:', err)
        setError('We could not load your resume right now. Try refreshing the page. If this continues, contact your strategist.')
      } finally {
        setLoading(false)
      }
    }

    fetchClient()
  }, [clientId])

  // The page's own shape rather than a centred spinner, which threw the
  // heading away and rebuilt it on every load.
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={26} />
        <Skeleton variant="text" width={300} height={20} sx={{ mb: 3 }} />
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={160} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={220} sx={{ borderRadius: '12px' }} />
        </Stack>
      </Box>
    )
  }

  if (error || !client) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error || 'Unable to load resume'}
      </Alert>
    )
  }

  const isUploaded = client.resumeSource === 'uploaded'
  const hasResume = isUploaded ? !!resumeFile : !!client.resumeContent && client.resumeContent !== ''
  const resumeHtml = !isUploaded && hasResume ? resumeToHtml(client.resumeContent as ResumeData) : ''
  const lastUpdated = isUploaded ? resumeFile?.createdAt : client.resumeGeneratedAt
  const isPublished = hasResume && client.resumeIsDraft !== true
  const isDraft = isPublished && client.resumePublishMode === 'draft'
  // Staff approval and the client's own are independent. Internal Approve used
  // to hide this button, but only the client's approval marks the resume
  // complete and moves Deliverables In Progress on, so an internally approved
  // resume sat in review with nothing left for anyone to click (MWA-371).
  const canApprove = isPublished && !isDraft && !client.resumeApproved

  return (
    <Box>
      {/* Suppressed when this renders as a tab of My Profile, which supplies
          the page heading. */}
      {!embedded && (
        <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4" component="h1">
          My Resume
        </Typography>
        <PageHelpButton articleId="page-portal-resume" />
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View your professionally crafted resume.
      </Typography>
        </>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Typography variant="h6">Resume Status</Typography>
            {hasResume ? (
              <Chip
                label={client.resumeGenerationStatus === 'generating' ? 'In progress' : 'Available'}
                color={client.resumeGenerationStatus === 'generating' ? 'warning' : 'success'}
              />
            ) : (
              <Chip label="Not started" color="default" />
            )}
            {client.resumeApproved && (
              <Chip
                icon={<ApproveIcon sx={{ fontSize: 16 }} />}
                label="Approved"
                color="success"
              />
            )}
            {isPublished && !isDraft && client.internalResumeApproved && !client.resumeApproved && (
              <Chip
                icon={<ApproveIcon sx={{ fontSize: 16 }} />}
                label="Approved by Staff"
                color="success"
              />
            )}
          </Box>

          {lastUpdated && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Last updated: {new Date(lastUpdated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          )}

          {canApprove && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 1.5, sm: 2 },
                  width: '100%',
                }}
              >
                <Typography variant="body2">
                  Please review your resume and approve it when you're satisfied.
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={approving ? <CircularProgress size={14} /> : <ApproveIcon />}
                  onClick={handleApprove}
                  disabled={approving}
                  sx={{ flexShrink: 0 }}
                >
                  Approve this version
                </Button>
              </Box>
            </Alert>
          )}

          {isDraft && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Your strategist is still working on this. Do not use it for applications yet.
              </Typography>
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {isPublished && !isUploaded && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              {isSmUp ? (
                <Button
                  variant="outlined"
                  startIcon={downloadingPdf ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                >
                  Download PDF
                </Button>
              ) : (
                <IconButton
                  color="primary"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                >
                  {downloadingPdf ? <CircularProgress size={20} /> : <DownloadIcon />}
                </IconButton>
              )}
            </Box>
          )}

          {fileError ? (
            <Alert severity="error">
              We could not load your resume file. Try refreshing the page. If this continues, contact
              your strategist.
            </Alert>
          ) : isUploaded && resumeFile ? (
            <ResumeFilePreview attachment={resumeFile} />
          ) : hasResume ? (
            <Paper
              ref={resumeRef}
              variant="outlined"
              sx={{
                p: 3,
                backgroundColor: 'background.paper',
                '& h2': {
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  mt: 3,
                  mb: 1,
                },
                '& h3': {
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  mt: 2,
                  mb: 0.5,
                },
                '& p': {
                  mb: 1,
                  lineHeight: 1.6,
                },
                '& ul': {
                  pl: 2,
                  mb: 1,
                },
                '& li': {
                  mb: 0.5,
                },
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: resumeHtml }} />
            </Paper>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Your resume is being prepared
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once your strategist finishes it, it will appear here.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Documents
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Documents prepared by your career team.
          </Typography>
          <AttachmentsTab
            entityId={client.id}
            entityType="resume_document"
            title="Documents"
            emptyStateText="No documents yet. Your strategist will share documents here as your engagement progresses."
            hideUpload
            hideIds={client.resumeAttachmentId ? [client.resumeAttachmentId] : undefined}
          />
        </CardContent>
      </Card>
    </Box>
  )
}
